import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useApexOnlyPage } from '../../hooks/useApexOnlyPage';
import { buildApexCanonicalUrl } from '../../lib/siteUrls';

const TermsConditions = () => {
  const isRedirecting = useApexOnlyPage('/terms');

  if (isRedirecting) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Terms of Use | Alakaʻi Aloha</title>
        <meta
          name="description"
          content="Review the Alakaʻi Aloha terms governing use of the apex domain, island subdomains, and digital trip-planning services."
        />
        <link rel="canonical" href={buildApexCanonicalUrl('/terms')} />
      </Helmet>

      <div className="max-w-4xl mx-auto py-4 px-6 sm:px-8">
        <header className="mb-8 space-y-2">
          <h1 className="text-3xl font-semibold">Terms &amp; Conditions</h1>
          <p className="text-base leading-relaxed text-white/80">
            These Terms &amp; Conditions govern the use of alakaialoha.com, our island subdomains, and any purchase
            of our digital itinerary services. By browsing the site or completing checkout, you agree to the policies
            outlined below.
          </p>
          <p className="text-sm text-white/60">
            <span className="font-medium">Last updated:</span> October 5, 2025
          </p>
        </header>

      <section className="space-y-5 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">1. Using the Site</h2>

        <h3 className="text-base font-semibold">1.1 Eligibility &amp; Access Links</h3>
        <p>
          You must be at least 16 to browse the site and at least 18 to complete a
          purchase. We do not issue traditional accounts; instead, interviews and itineraries are tied to unique access
          links. Keep these links private—anyone with the URL can view your content. If you need an access link rotated
          or disabled, contact us through the <a href="/support" className="underline">Support page</a>.
        </p>

        <h3 className="text-base font-semibold">1.2 Acceptable Use</h3>
        <p>
          Do not disrupt, overload, or attempt to reverse-engineer the site. Automated scraping, bulk data extraction,
          or security testing requires our written consent.
        </p>

        <h3 className="text-base font-semibold">1.3 Intellectual Property</h3>
        <p>
          All site content—text, layouts, data models, and the Alakaʻi Aloha name and marks—belongs to us or our
          licensors. You may not resell, republish, or create derivative works except for the personal license granted
          when you purchase an itinerary.
        </p>

        <h3 className="text-base font-semibold">1.4 AI‑Generated Guidance</h3>
        <p>
          We blend human curation with AI-assisted writing. Conditions, hours, pricing, and availability can change
          quickly. Treat the itinerary as planning guidance and verify time-sensitive information directly with venues,
          operators, and authorities.
        </p>

        <h3 className="text-base font-semibold">1.5 Copyright Notices</h3>
        <p>
          Alleged copyright infringement notifications should be submitted through our <a href="/support" className="underline">Support page</a>. Include your contact details, the copyrighted
          work, the specific URL in question, and a good-faith statement.
        </p>
      </section>

      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80" id="sale">
        <h2 className="text-xl font-semibold">2. Purchasing a Digital Itinerary</h2>

        <h3 className="text-base font-semibold">2.1 What You Receive</h3>
        <p>
          Each purchase unlocks a personalized itinerary generated from your interview responses and our editorial
          content. Delivery is entirely digital—no physical goods ship.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Scope:</strong> up to 15 days of suggestions, including daily schedules, dining ideas, and key
            logistics.
          </li>
          <li>
            <strong>Access window:</strong> continue viewing and editing for 12 months after purchase.
          </li>
          <li>
            <strong>Formats:</strong> Interactive web view, with optional exports as new features launch.
          </li>
        </ul>

        <h3 className="text-base font-semibold">2.3 Personal License</h3>
        <p>
          You receive a limited, non-transferable license for personal travel planning. Sharing publicly, reselling,
          reposting, or using the itinerary to build competing services is not permitted.
        </p>

        <h3 className="text-base font-semibold">2.4 Third-Party Dependencies</h3>
        <p>
          Your itinerary references third-party providers (tours, restaurants, maps). We do not control their
          operations. Always confirm availability, permits, weather conditions, and safety information with official
          sources before traveling.
        </p>
      </section>

      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80" id="refunds">
        <h2 className="text-xl font-semibold">3. Refunds &amp; Cancellations</h2>
        <p>
          Because itineraries are personalized digital goods delivered immediately, <strong>all sales are final</strong>.
          Still, we aim for a great experience:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>If we cannot deliver:</strong> If your itinerary fails to generate or becomes inaccessible within 1 hour, we will attempt to regenerate it or issue a refund at our discretion.
          </li>
          <li>
            <strong>If information becomes outdated:</strong> We will regenerate or adjust the plan at no additional
            cost when you report meaningful issues.
          </li>
          <li>
            <strong>How to request help:</strong> Submit your order email and description of the issue via the <a href="/support" className="underline">Support page</a>.
          </li>
        </ul>
      </section>

      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">4. Disclaimers</h2>
        <p>
          Travel involves inherent risks. Conditions change rapidly—especially outdoors and along the coast. Our
          content is provided “as is” and does not replace professional advice. Always follow posted warnings, local
          regulations, and official guidance.
        </p>
      </section>

      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">5. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, we are not liable for indirect, incidental, or consequential damages,
          including lost profits or data, arising from your use of the site or itinerary. Our total liability is limited
          to the amount you paid for the itinerary in question.
        </p>
      </section>

      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">6. Indemnification</h2>
        <p>
          You agree to indemnify and hold us harmless from claims resulting from your misuse of the site, violation of
          these Terms, or infringement of third-party rights.
        </p>
      </section>

      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">7. Updates to These Terms</h2>
        <p>
          We may update the Service or these Terms as our offerings evolve. Material changes will be noted on this page
          with a revised “Last updated” date. Continued use after changes means you accept the updated terms.
        </p>
      </section>

        <section className="space-y-3 mt-12 text-sm leading-relaxed text-white/80">
          <h2 className="text-xl font-semibold">8. Contact</h2>
          <p>
            Questions about these Terms? Reach us through the <a href="/support" className="underline">Support page</a>.
          </p>
        </section>
      </div>
    </>
  );
};

export default TermsConditions;
